import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { environment } from "../../configuration/environment";
import { createUploadLink } from "apollo-upload-client";

type Level = "primaria" | "secundaria";

interface ApolloClientContextType {
  level: Level;
  setLevel: (level: Level) => void;
  client: ApolloClient<any>;
}

const ApolloClientContext = createContext<ApolloClientContextType | undefined>(undefined);

const APOLLO_KEY_PRIMARIA = process.env.REACT_APP_APOLLO_KEY_PRIMARIA;
const APOLLO_KEY_SECUNDARIA = process.env.REACT_APP_APOLLO_KEY_SECUNDARIA;
const GRAPHQL_URL_PRIMARIA = environment.primariaGraphQLURL;
const GRAPHQL_URL_SECUNDARIA = environment.secundariaGraphQLURL;

const LEVEL_STORAGE_KEY = "luca2-level";

function getApolloConfig(level: Level) {
  if (level === "primaria") {
    if (!GRAPHQL_URL_PRIMARIA || !APOLLO_KEY_PRIMARIA) {
      throw new Error("Missing REACT_APP_GRAPHQL_URL_PRIMARIA or REACT_APP_APOLLO_KEY_PRIMARIA in environment");
    }
    return {
      uri: GRAPHQL_URL_PRIMARIA,
      apiKey: APOLLO_KEY_PRIMARIA,
    };
  } else {
    if (!GRAPHQL_URL_SECUNDARIA || !APOLLO_KEY_SECUNDARIA) {
      throw new Error("Missing REACT_APP_GRAPHQL_URL_SECUNDARIA or REACT_APP_APOLLO_KEY_SECUNDARIA in environment");
    }
    return {
      uri: GRAPHQL_URL_SECUNDARIA,
      apiKey: APOLLO_KEY_SECUNDARIA,
    };
  }
}

interface ApolloClientProviderProps {
  children: ReactNode;
}

export const ApolloClientProvider: React.FC<ApolloClientProviderProps> = ({ children }) => {
  const [level, setLevelState] = useState<Level>(() => {
    const stored = localStorage.getItem("luca2-level");
    return stored === "secundaria" ? "secundaria" : "primaria";
  });

  const setLevel = (newLevel: Level) => {
    setLevelState(newLevel);
    localStorage.setItem("luca2-level", newLevel);
    localStorage.removeItem("token");
    window.location.reload();
  };

  const { uri, apiKey } = getApolloConfig(level);

  const client = useMemo(() => {
    // Auth link to add JWT token from localStorage
    const authLink = setContext((_, { headers }) => {
      const token = localStorage.getItem("token");
      return {
        headers: {
          ...headers,
          token: token ? token : "",
        },
      };
    });

    const uploadLink = createUploadLink({
      uri,
      headers: {
        "x-api-key": apiKey,
        "Content-Type": "application/json",
      },
    });

    return new ApolloClient({
      link: ApolloLink.from([authLink, uploadLink]),
      cache: new InMemoryCache(),
    });
  }, [uri, apiKey]);


  return (
    <ApolloClientContext.Provider value={{ level, setLevel, client }}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloClientContext.Provider>
  );
};

export function useApolloClientContext() {
  const ctx = useContext(ApolloClientContext);
  if (!ctx) throw new Error("useApolloClientContext must be used within ApolloClientProvider");
  return ctx;
}
